import type { TPlayer } from '@setplex/pureya'
import type { PlayerMeta } from '../player-meta'

import { useEffect } from 'react'
import { Intent, PlayerIntentMessage } from '~/entities/player'
import { CONTENT_TYPE } from '~/shared/constants/content'
import { mq } from '~/shared/mq'
import { PlayerMetaMessage, PlayerMountedMessage } from '../player-meta'

/**
 * UI bridge from React player components into analytics MQ contracts:
 * emits mount/unmount lifecycle + normalized content metadata updates.
 */
export type PlayerMetaEx = Omit<PlayerMeta, 'type' | 'trailer' | 'paid'> & {
  type: CONTENT_TYPE
  trailer?: boolean
  paid?: boolean
  autoplayTrailer?: boolean
  trailerCategoryName?: string
}

export const useEmitPlayerMeta = (meta: PlayerMetaEx, player?: TPlayer) => {
  // emit player mounted/unmounted events
  useEffect(() => {
    if (player) {
      mq.emit(new PlayerMountedMessage(player, true))
      mq.emit(
        new PlayerIntentMessage(
          player,
          meta.autoplayTrailer ? Intent.AutoPreview : Intent.OpenPlayer
        )
      )
      return () => {
        mq.emit(new PlayerIntentMessage(player, Intent.ClosePlayer))
        mq.emit(new PlayerMountedMessage(player, false))
      }
    }
  }, [player, meta.autoplayTrailer])

  // emit player meta updates
  useEffect(() => {
    const { id, type, url, name, program, trailer, paid, trailerCategoryName } =
      meta

    // normalize content type
    // prettier-ignore
    const normalizedType: PlayerMeta['type'] = (() => {
      switch (type) {
        case CONTENT_TYPE.TV:      return 'tv'
        case CONTENT_TYPE.VOD:     return 'vod'
        case CONTENT_TYPE.SHOW:    return 'tvshow'
        case CONTENT_TYPE.CATCHUP: return 'catchup'
        case CONTENT_TYPE.EVENT:   return 'event'
        default:                   return (type as CONTENT_TYPE).toLowerCase() as PlayerMeta['type']
      }
    })()

    const payload: PlayerMeta = {
      id,
      type: normalizedType,
      url,
      name,
      program,
      paid: paid ?? false,
      trailer: trailer ?? false,
      trailerCategoryName,
    }

    mq.emit(new PlayerMetaMessage(payload))

    // this is not honestly correct, to specify dependency array like this,
    // but it works for our case, since meta is an object with primitive values,
    // and list of fields and their orders does not change
    // oxlint-disable-next-line react/exhaustive-deps
  }, Object.values(meta))
}
